import 'chosen-js/chosen.jquery';
import 'chosen-js/chosen.min.css';

import autobind from 'class-autobind';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';
import sortBy from 'lodash/sortBy';

import JockeyTrainerReductionTypes from 'atg/constants/JockeyTrainerReductionTypes';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import * as Text from 'shared/utils/Text';

import { createHelpIcon } from '../HelpIcon';
import BaseView from './BaseView';

const $ = window.$;

export const TYPE_JOCKEY = 'JOCKEY';
export const TYPE_TRAINER = 'TRAINER';

export default class JockeyTrainerReductionView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, JockeyTrainerReductionView.prototype);
  }

  initializeAsViewType(viewType) {
    // Since Jockey Reduction and Trainer Reduction are effectively identical
    // they are both handled within this single Reduction View.
    // This view is subclassed for specific functionality, but to differentiate within
    // this class, the following booleans are used. Subclass is expected to
    // call this 'initializeAsViewType' immediately after the constructor.
    this.isJockeyView = viewType === TYPE_JOCKEY;
    this.isTrainerView = viewType === TYPE_TRAINER;
  }

  setup() {}

  // ---------------------------------------- MENU ----------------------------------------

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  // ---------------------------------------- GRID ----------------------------------------
  onRowWasUpdated() {}
  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();
    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let betConfig;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      betConfig = rowCache.betConfig;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};

      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;
    const start = race.getStart(startIndex);
    const info = {
      rowCache: rowCache,
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockeyTrainer = this.isJockeyView
        ? start.getJockey()
        : start.getTrainer();
      if (jockeyTrainer) {
        info.jockeyTrainerName = jockeyTrainer.getName();
        if (!this.isLimitedReduction()) {
          info.bottomInfo = `${Math.round(
            jockeyTrainer.getLastTwoYearsWinPercent(),
          )}%`;
        }
      }

      if (this.isJockeyView) {
        const originalJockey = start.getOriginalJockey();
        if (originalJockey) {
          info.originalJockey = originalJockey.getName();
        }
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    return info;
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
      ...this.makeHorseButtons(position),
    ];

    this.getGrid().showCellButtons(position, buttons);
  }

  // ---------------------------------------- DATA ----------------------------------------

  isLimitedReduction() {
    const betInformation = this.getAPI().betInformation;
    return (
      IS_NO_SITE ||
      betInformation.isGallop() ||
      betInformation.hasInternationalTrack()
    );
  }

  isLimitedByInternationalTrack() {
    const betInformation = this.getAPI().betInformation;
    return (
      IS_SE_SITE &&
      !betInformation.isGallop() &&
      betInformation.hasInternationalTrack()
    );
  }

  getReduction() {
    // Implemented by subclass
  }

  getItem() {
    return this.getReduction();
  }

  getJockeyTrainerItems() {
    const raceInformation = this.getAPI().raceInformation;

    const jockeyTrainerItems = [];

    const betType = raceInformation.getBetType();
    const betTypeString = Text.get(`bet-type-short_${betType.toLowerCase()}`);

    const races = raceInformation.getRaces();

    for (const [raceIndex, race] of races.entries()) {
      const starts = race.getStarts();

      for (const [startIndex, start] of starts.entries()) {
        const jockeyTrainer = this.isJockeyView
          ? start.getJockey()
          : start.getTrainer();

        if (!jockeyTrainer) {
          continue;
        }

        const jockeyTrainerID = jockeyTrainer.getIDString();
        let jockeyTrainerItem = find(jockeyTrainerItems, {
          id: jockeyTrainerID,
        });
        if (!jockeyTrainerItem) {
          jockeyTrainerItem = {
            id: jockeyTrainerID,
            displayName: jockeyTrainer.getName(),
            firstName: jockeyTrainer.getFirstName(),
            lastName: jockeyTrainer.getLastName(),
            hometrack: jockeyTrainer.getHometrack(),
            percentWin: jockeyTrainer.getWinPercent(),
            nrOfStarts: jockeyTrainer.getNumberOfStarts(),
            lastTwoYearsWinPercent: jockeyTrainer.getLastTwoYearsWinPercent(),
            roundedLastTwoYearsWinPercent: Math.round(
              jockeyTrainer.getLastTwoYearsWinPercent(),
            ),
            starts: [],
            jstarts: [],
          };
          jockeyTrainerItems.push(jockeyTrainerItem);
        }

        jockeyTrainerItem.starts.push(
          `${betTypeString}-${raceIndex + 1} (${startIndex + 1})`,
        );
        const jstart = { legNr: raceIndex + 1, startNr: startIndex + 1 };
        jockeyTrainerItem.jstarts.push(jstart);
      }
    }

    return jockeyTrainerItems;
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoContent() {
    // Hidden on gallop tracks and on foreign tracks
    const isHidden = this.isLimitedReduction();

    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-jockey-trainer-reduction',
      {
        '%jockey-trainer-conditions%': Text.get(
          this.isJockeyView
            ? 'bottom-conditions-jockey-conditions'
            : 'bottom-conditions-trainer-conditions',
        ),
        '%jockey-trainer-conditions-hometrack%': Text.get(
          'bottom-conditions-jockey-trainer-hometrack',
        ),
        '%jockey-trainer-conditions-victory-rich%': Text.get(
          'bottom-conditions-jockey-trainer-victory-rich',
        ),
        '%jockey-trainer-conditions-not-victory-rich%': Text.get(
          'bottom-conditions-jockey-trainer-not-victory-rich',
        ),
        '%jockey-trainer-conditions-win-percent%': Text.get(
          'bottom-conditions-jockey-trainer-win-percent',
        ),
        '%hide%': isHidden ? 'hidden' : '',
      },
    ));

    $conditions.find('.jockey-trainer-reduction h2').append(
      createHelpIcon({
        header: this.isJockeyView
          ? Text.get('help-jockey-reduction-bottom-header')
          : Text.get('help-trainer-reduction-bottom-header'),
        body: this.isJockeyView
          ? Text.get('help-jockey-reduction-bottom-body')
          : Text.get('help-trainer-reduction-bottom-body'),
      }),
    );

    $conditions.find('.jockey-trainer-conditions-hometrack h2').append(
      createHelpIcon({
        header: Text.get('help-jockey-trainer-hometrack-header'),
        body: this.isJockeyView
          ? Text.get('help-jockey-hometrack-body')
          : Text.get('help-trainer-hometrack-body'),
      }),
    );

    $conditions.find('.jockey-trainer-conditions-victory-rich h2').append(
      createHelpIcon({
        header: Text.get('help-jockey-trainer-victory-rich-header'),
        body: this.isJockeyView
          ? Text.get('help-jockey-victory-rich-body')
          : Text.get('help-trainer-victory-rich-body'),
      }),
    );

    $conditions.find('.jockey-trainer-conditions-not-victory-rich h2').append(
      createHelpIcon({
        header: Text.get('help-jockey-trainer-not-victory-rich-header'),
        body: this.isJockeyView
          ? Text.get('help-jockey-not-victory-rich-body')
          : Text.get('help-trainer-not-victory-rich-body'),
      }),
    );

    $conditions.find('.jockey-trainer-conditions-win-percent h2').append(
      createHelpIcon({
        header: Text.get('help-jockey-trainer-win-percent-header'),
        body: this.isJockeyView
          ? Text.get('help-jockey-win-percent-body')
          : Text.get('help-trainer-win-percent-body'),
      }),
    );

    $conditions.find('form').event('submit', this.onFormSubmit);

    this.initializeRows();
    this.updateConditionsJockeyTrainerRows();
    if (!isHidden) {
      this.updateHometrackJockeyTrainerRows();
      this.updateTopJockeyTrainerRows();
      this.updateLowJockeyTrainerRows();
      this.updateJockeyTrainerWinPercent();
    } else if (this.isLimitedByInternationalTrack()) {
      $conditions.find('.jockey-trainer-reduction').css('height', '150px');
      $conditions
        .find('.jockey-trainer-conditions-international-track-warning')
        .text(
          this.isJockeyView
            ? Text.get('bottom-conditions-jockey-international-track-warning')
            : Text.get('bottom-conditions-trainer-international-track-warning'),
        )
        .css('height', '200px')
        .css('display', 'block');
    } else {
      $conditions.find('.jockey-trainer-reduction').css('height', '350px');
    }

    return $conditions;
  }

  getConditionsRowType() {
    return this.isJockeyView
      ? JockeyTrainerReductionTypes.JOCKEY_CONDITIONS
      : JockeyTrainerReductionTypes.TRAINER_CONDITIONS;
  }

  getPercentRowType() {
    return this.isJockeyView
      ? JockeyTrainerReductionTypes.JOCKEY_PERCENT
      : JockeyTrainerReductionTypes.TRAINER_PERCENT;
  }

  initializeRows() {
    const reductionRows = [
      this.getConditionsRowType(),
      JockeyTrainerReductionTypes.HOMETRACK,
      JockeyTrainerReductionTypes.VICTORY_RICH,
      JockeyTrainerReductionTypes.OUT_OF_TOP_TEN,
      this.getPercentRowType(),
    ];
    for (const reductionRow of reductionRows) {
      const rows = this._getReductionRow(reductionRow);
      if (rows.length === 0) {
        const newRow = this.getReduction().addJockeyTrainerGroupRow();
        newRow.setGroupType(reductionRow);
      }
    }
  }
  // ---------------------------------------- POINT SUMS ----------------------------------------

  updateConditionsJockeyTrainerRows() {
    const reduction = this.getReduction();
    const raceInformation = this.getAPI().raceInformation;
    const numRaces = raceInformation.getNumberOfRaces();
    const conditionsRowType = this.getConditionsRowType();
    const conditionsRows = reduction
      .getJockeyTrainerGroupRows()
      .filter(row => row.getGroupType() === conditionsRowType);
    const numberOfRows = Math.max(1, conditionsRows.length);

    // Create a sorted array for the select element
    // containing all the jockeys or trainers
    const jockeyTrainerOptions = [
      { value: '', text: '' },
      ...sortBy(this.getJockeyTrainerItems(), ['lastName', 'firstName']).map(
        item => ({
          value: item.id,
          text: `${item.lastName} ${item.firstName}: ${item.starts.join(', ')}`,
        }),
      ),
    ];

    const $wrapper = this.$conditions.find('.jockey-trainer-reduction');

    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const conditionsRow = conditionsRows[n];
      const $row = StickyTemplate.jq(
        'bottom-conditions-jockey-trainer-reduction-row',
        {
          '%Min%': Text.get('bottom-conditions-Min'),
          '%max%': Text.get('bottom-conditions-max'),
          '%races-won-by%': Text.get(
            'bottom-conditions-jockey-trainer-conditions-races-won-by',
          ),
          '%dropdown-selection-instructions%': Text.get(
            this.isJockeyView
              ? 'bottom-conditions-jockey-selection-instructions'
              : 'bottom-conditions-trainer-selection-instructions',
          ),
          '%num-races%': numRaces,
          '%row%': n,
          '%uuid%': conditionsRow.getUUID(),
        },
      );

      this.addNumberOfRacesInput($row);

      if (
        !this.updatedJockeyReductions ||
        isEmpty(this.updatedJockeyReductions)
      ) {
        this.setSelectOptions(
          $row.find('.jockey-trainer-reduction'),
          jockeyTrainerOptions,
        );
      } else if (conditionsRow) {
        forEach(
          this.updatedJockeyReductions,
          // TODO Fix lint error
          // eslint-disable-next-line no-loop-func
          function (updatedJockey) {
            if (updatedJockey.reductionRowId === conditionsRow.getId()) {
              $row.find('.button.delete').nextAll().remove();
              $row.append(
                $('<span>', {
                  class: 'jockey-trainer-reduction-isdefunct',
                  text: Text.get(
                    'bottom-conditions-jockey-conditions-isdefunct',
                  ),
                }),
              );
            } else {
              this.setSelectOptions(
                $row.find('.jockey-trainer-reduction'),
                jockeyTrainerOptions,
              );
            }
          }.bind(this),
        );
      }

      if (conditionsRow) {
        $row
          .find('.min-races')
          .val(Utils.negativeBlankToValue(conditionsRow.getMinNumberOfRaces()));
        $row
          .find('.max-races')
          .val(Utils.negativeBlankToValue(conditionsRow.getMaxNumberOfRaces()));
        const jockeyTrainerIds = conditionsRow
          .getJockeyTrainers()
          .map(item => item.getId());
        $row.find(`.jockey-trainer-${n}`).val(jockeyTrainerIds);
      }

      const chosenSelect = $row.find(`.jockey-trainer-${n}`).chosen({
        width: '350px',
        placeholder_text_multiple: Text.get(
          this.isJockeyView
            ? 'bottom-conditions-jockey-selection-placeholder'
            : 'bottom-conditions-trainer-selection-placeholder',
        ),
      });

      const isMobile = chosenSelect.data('chosen') === undefined;
      if (isMobile) {
        chosenSelect.css('width', '100%');
        chosenSelect.css('padding', '0 15px 0');
        chosenSelect.css('background-color', 'white');
        chosenSelect.css('-webkit-appearance', 'none');
        chosenSelect.css('border-radius', 0);
      }
      chosenSelect.reevent('chosen:update', () => {
        if (chosenSelect.attr('disabled')) {
          const wasDisabled = chosenSelect.is(':disabled');
          chosenSelect.data('chosen').search_field_disabled();

          if (wasDisabled) {
            chosenSelect.attr('disabled', true);
          } else {
            chosenSelect.removeAttr('disabled');
          }
        }
      });
      $row.find('input, select').reevent('change', (evt, params) => {
        const data = params || {};
        data['type'] = conditionsRowType;
        data['rowId'] = $(evt.currentTarget)
          .closest('.row')
          .attr('data-row-uuid');
        const jockeyTrainerIds = [];

        forEach(chosenSelect.val(), jockeyTrainerId =>
          jockeyTrainerIds.push({ value: jockeyTrainerId }),
        );
        data[conditionsRowType] = [...jockeyTrainerIds];
        delete data.selected;
        delete data.deselected;
        data['isMobile'] = isMobile;
        this.onConditionsChanged(evt, data);
        if (jockeyTrainerIds.length > 0 && params) {
          chosenSelect.attr('disabled', true).trigger('chosen:update');
        }
      });
      $rows.append($row);
    }

    $wrapper.find('.button.plus').reevent('click', this.onAddRowPressed);
    $wrapper.find('.button.delete').reevent('click', this.onDeleteRowPressed);

    this.attachNegativeBlankEvents($wrapper);
  }

  onAddRowPressed() {
    let groupRow;
    const reduction = this.getReduction();
    const conditionsRowType = this.getConditionsRowType();
    const conditionsRows = reduction
      .getJockeyTrainerGroupRows()
      .filter(row => row.getGroupType() === conditionsRowType);
    if (conditionsRows.length === 0) {
      groupRow = reduction.addJockeyTrainerGroupRow();
      groupRow.setGroupType(conditionsRowType);
    }

    groupRow = reduction.addJockeyTrainerGroupRow();
    groupRow.setGroupType(conditionsRowType);

    this.getAPI().scheduleBetUpdate();

    this.updateConditionsJockeyTrainerRows();

    const $row = this.$conditions.find(
      '.jockey-reduction .rows .row:last-child',
    );
    $row.hide();
    $row.slideDown(300);
  }

  onDeleteRowPressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const uid = $row.attr('data-row-uuid');
    const conditionsRowType = this.getConditionsRowType();

    const reduction = this.getReduction();

    reduction.deleteJockeyTrainerGroupRow(uid);
    const jockeyTrainerRows = reduction
      .getJockeyTrainerGroupRows()
      .filter(row => row.getGroupType() === conditionsRowType);

    if (jockeyTrainerRows.length === 0) {
      const newRow = reduction.addJockeyTrainerGroupRow();
      newRow.setGroupType(conditionsRowType);
      jockeyTrainerRows.push(newRow);
    }

    this.getAPI().scheduleBetUpdate();

    if (jockeyTrainerRows.length === 0) {
      this.updateConditionsJockeyTrainerRows();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateConditionsJockeyTrainerRows();
        }.bind(this),
      );
    }
  }

  updateHometrackJockeyTrainerRows() {
    const $wrapper = this.$conditions.find(
      '.jockey-trainer-conditions-hometrack',
    );

    this.filterHometrackJockeys(this.getJockeyTrainerItems())
      .then(homeJockeyTrainerOptions => {
        const jockeyTrainerOptions = [
          {
            value: '-1',
            text: Text.get(
              this.isJockeyView
                ? 'bottom-conditions-jockey-dropdown-placeholder'
                : 'bottom-conditions-trainer-dropdown-placeholder',
            ),
          },
          ...homeJockeyTrainerOptions,
        ];

        const $rows = $wrapper.find('.rows');

        $rows.empty();

        if (jockeyTrainerOptions.length === 1) {
          $wrapper[0].classList.add('hidden');
          return;
        }

        const hometrackRows = this._getReductionRow(
          JockeyTrainerReductionTypes.HOMETRACK,
        );
        const hometrackRow = hometrackRows[0];
        const $row = StickyTemplate.jq(
          'bottom-conditions-jockey-trainer-hometrack-row',
          {
            '%min%': 'Min:',
            '%max%': 'Max:',
            '%of-races%': Text.get('bottom-conditions-of-races'),
            '%races-won-by%': Text.get(
              this.isJockeyView
                ? 'bottom-conditions-jockey-hometrack-races-won-by'
                : 'bottom-conditions-trainer-hometrack-races-won-by',
            ),
            '%row%': 0,
            '%uuid%': hometrackRow.getUUID(),
          },
        );

        this.addNumberOfRacesInput($row);

        if (
          !this.updatedJockeyReductions ||
          isEmpty(this.updatedJockeyReductions)
        ) {
          this.setSelectOptions(
            $row.find('.jockey-trainer-hometrack'),
            jockeyTrainerOptions,
          );
        }

        if (hometrackRow) {
          $row
            .find('.min-races')
            .val(
              Utils.negativeBlankToValue(hometrackRow.getMinNumberOfRaces()),
            );
          $row
            .find('.max-races')
            .val(
              Utils.negativeBlankToValue(hometrackRow.getMaxNumberOfRaces()),
            );
        }
        $rows.append($row);

        const params = {};
        params[JockeyTrainerReductionTypes.HOMETRACK] = [
          ...jockeyTrainerOptions,
        ];
        params['type'] = JockeyTrainerReductionTypes.HOMETRACK;

        $wrapper.find('select').reevent('change', evt => {
          const hometrackRows = this._getReductionRow(
            JockeyTrainerReductionTypes.HOMETRACK,
          );
          params['rowId'] = hometrackRows[0].getUUID();
          this.onConditionsChanged(evt, params);
        });
        $wrapper
          .find('select.jockey')
          .reevent(
            'change',
            this.doNothing.bind(null, $wrapper.find('select.jockey')),
          );
        this.attachNegativeBlankEvents($wrapper);
      })
      .catch(error => {
        $wrapper[0].classList.add('hidden');
        console.error(error);
      });
  }

  filterHometrackJockeys(jockeyItems) {
    const betKey = this.getAPI().betInformation.getBetKey();
    return new Promise((resolve, reject) => {
      this.getAPI().loadBetKeyInfo(betKey, function (betKeyInfo) {
        if (!betKeyInfo) {
          return reject(new Error('Error loading betKeyInfo'));
        }

        const track = betKeyInfo.getTrack().data;
        const trackName = track.domesticText.split('/');

        // Create a sorted array for the select element
        // containing jockeys that have the same home track as the event.
        const hometrackJockeys = orderBy(jockeyItems, ['lastName'], ['asc'])
          .filter(item => {
            let result;
            if (trackName.length > 1) {
              if (item.hometrack === trackName[0]) {
                result = item.jstarts.every(race => race.legNr % 2 === 0);
              }
              if (item.hometrack === trackName[1]) {
                result = item.jstarts.every(race => race.legNr % 2 !== 0);
              }
            }
            if (trackName.length === 1) {
              result = item.hometrack === trackName[0];
            }
            return result;
          })
          .map(item => ({
            value: item.id,
            text: `${item.lastName} ${item.firstName} ${item.starts.join(
              ', ',
            )}`,
          }));

        resolve(hometrackJockeys);
      });
    });
  }

  doNothing(element) {
    element.val(['-1']);
  }

  updateTopJockeyTrainerRows() {
    const victoryRichRows = this._getReductionRow(
      JockeyTrainerReductionTypes.VICTORY_RICH,
    );

    // Create a sorted array for the select element
    // containing jockeys that have from 0 to many victories and the starts are
    // >= 100, second sorting parameter is number of starts
    const jockeyList = this.getJockeyTrainerItems().sort((a, b) => {
      const firstVal = a.roundedLastTwoYearsWinPercent;
      const secondVal = b.roundedLastTwoYearsWinPercent;

      if (secondVal === firstVal) {
        return b.nrOfStarts > a.nrOfStarts ? 1 : -1;
      }

      return secondVal > firstVal ? 1 : -1;
    });

    const jockeyTrainerOptions = [
      {
        value: '-1',
        text: Text.get(
          this.isJockeyView
            ? 'bottom-conditions-jockey-dropdown-placeholder'
            : 'bottom-conditions-trainer-dropdown-placeholder',
        ),
      },
      ...jockeyList
        .filter(item => item.nrOfStarts >= 100)
        .map(item => ({
          value: item.id,
          text: `${item.displayName}: ${item.roundedLastTwoYearsWinPercent}%`,
        }))
        .slice(0, 5),
    ];

    const $wrapper = this.$conditions.find(
      '.jockey-trainer-conditions-victory-rich',
    );
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    if (jockeyTrainerOptions.length === 1) {
      $wrapper[0].classList.add('hidden');
      return;
    }

    const victoryRichRow = victoryRichRows[0];
    const $row = StickyTemplate.jq(
      'bottom-conditions-jockey-trainer-victory-rich-row',
      {
        '%min%': Text.get('bottom-conditions-Min'),
        '%max%': Text.get('bottom-conditions-max'),
        '%jockeys-victory-rich%': Text.get(
          this.isJockeyView
            ? 'bottom-conditions-jockey-victory-rich-text'
            : 'bottom-conditions-trainer-victory-rich-text',
        ),
        '%row%': 0,
        '%uuid%': victoryRichRow.getUUID(),
      },
    );

    this.addNumberOfRacesInput($row);

    if (
      !this.updatedJockeyReductions ||
      isEmpty(this.updatedJockeyReductions)
    ) {
      this.setSelectOptions(
        $row.find('.jockey-victory-rich'),
        jockeyTrainerOptions,
      );
    }

    if (victoryRichRow) {
      // var min = victoryRichRow ? victoryRichRow.getMinNumberOfRaces() : -1;
      // var max = victoryRichRow ? victoryRichRow.getMaxNumberOfRaces() : -1;
      $row
        .find('.min-races')
        .val(Utils.negativeBlankToValue(victoryRichRow.getMinNumberOfRaces()));
      $row
        .find('.max-races')
        .val(Utils.negativeBlankToValue(victoryRichRow.getMaxNumberOfRaces()));
    }

    $rows.append($row);

    const params = {};
    params[JockeyTrainerReductionTypes.VICTORY_RICH] = [
      ...jockeyTrainerOptions,
    ];
    params['type'] = JockeyTrainerReductionTypes.VICTORY_RICH;

    $wrapper.find('select').reevent('change', evt => {
      const victoryRichRows = this._getReductionRow(
        JockeyTrainerReductionTypes.VICTORY_RICH,
      );
      params['rowId'] = victoryRichRows[0].getUUID();
      this.onConditionsChanged(evt, params);
    });
    $wrapper
      .find('select.jockey')
      .reevent(
        'change',
        this.doNothing.bind(null, $wrapper.find('select.jockey')),
      );
    this.attachNegativeBlankEvents($wrapper);
  }

  updateLowJockeyTrainerRows() {
    const outOfTop10Rows = this._getReductionRow(
      JockeyTrainerReductionTypes.OUT_OF_TOP_TEN,
    );

    // Create a sorted array for the select element
    // containing jockeys that have from 0 to many victories and the starts are >= 25

    const jockeyTrainerOptions = [
      { value: '', text: '' },
      ...orderBy(this.getJockeyTrainerItems(), k => k.lastTwoYearsWinPercent, [
        'desc',
      ])
        .filter(item => item.nrOfStarts >= 25)
        .map(item => ({
          value: item.id,
          text: `${item.displayName}: ${item.roundedLastTwoYearsWinPercent}%`,
        }))
        .slice(5),
    ];

    const jockeyTrainerNotVictoryRichItems = jockeyTrainerOptions.slice(-10);
    jockeyTrainerNotVictoryRichItems.unshift({
      value: '-1',
      text: Text.get(
        this.isJockeyView
          ? 'bottom-conditions-jockey-dropdown-placeholder'
          : 'bottom-conditions-trainer-dropdown-placeholder',
      ),
    });
    const $wrapper = this.$conditions.find(
      '.jockey-trainer-conditions-not-victory-rich',
    );
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    const lowWinJockeyTrainerRow = outOfTop10Rows[0];
    const $row = StickyTemplate.jq(
      'bottom-conditions-jockey-trainer-not-victory-rich-row',
      {
        '%min%': Text.get('bottom-conditions-Min'),
        '%max%': Text.get('bottom-conditions-max'),
        '%jockey-trainers-not-victory-rich%': Text.get(
          this.isJockeyView
            ? 'bottom-conditions-jockey-not-victory-rich-text'
            : 'bottom-conditions-trainer-not-victory-rich-text',
        ),
        '%row%': 0,
        '%uuid%': lowWinJockeyTrainerRow.getUUID(),
      },
    );

    this.addNumberOfRacesInput($row);

    if (
      !this.updatedJockeyReductions ||
      isEmpty(this.updatedJockeyReductions)
    ) {
      this.setSelectOptions(
        $row.find('.jockey-not-victory-rich'),
        jockeyTrainerNotVictoryRichItems,
      );
    }

    if (lowWinJockeyTrainerRow) {
      $row
        .find('.min-races')
        .val(
          Utils.negativeBlankToValue(
            lowWinJockeyTrainerRow.getMinNumberOfRaces(),
          ),
        );
      $row
        .find('.max-races')
        .val(
          Utils.negativeBlankToValue(
            lowWinJockeyTrainerRow.getMaxNumberOfRaces(),
          ),
        );
    }

    $rows.append($row);

    const params = {};
    params[JockeyTrainerReductionTypes.OUT_OF_TOP_TEN] = [
      ...jockeyTrainerNotVictoryRichItems,
    ];
    params['type'] = JockeyTrainerReductionTypes.OUT_OF_TOP_TEN;

    $wrapper.find('select').reevent('change', evt => {
      const outOfTop10Rows = this._getReductionRow(
        JockeyTrainerReductionTypes.OUT_OF_TOP_TEN,
      );
      params['rowId'] = outOfTop10Rows[0].getUUID();
      this.onConditionsChanged(evt, params);
    });
    $wrapper
      .find('select.jockey')
      .reevent(
        'change',
        this.doNothing.bind(null, $wrapper.find('select.jockey')),
      );
    this.attachNegativeBlankEvents($wrapper);
  }

  updateJockeyTrainerWinPercent() {
    const percentRowType = this.getPercentRowType();
    const percentBetween = this._getReductionRow(percentRowType);
    const numberOfRows = Math.max(1, percentBetween.length);

    const $wrapper = this.$conditions.find(
      '.jockey-trainer-conditions-win-percent',
    );
    const $rows = $wrapper.find('.rows');

    $rows.empty();

    for (let n = 0; n < numberOfRows; n++) {
      const percentWinsRow = percentBetween[n];
      const $row = StickyTemplate.jq(
        'bottom-conditions-jockey-trainer-win-percent-row',
        {
          '%min%': Text.get('bottom-conditions-Min'),
          '%max%': Text.get('bottom-conditions-max'),
          '%jockey-trainer-win-percent%': Text.get(
            this.isJockeyView
              ? 'bottom-conditions-jockey-win-percent-text'
              : 'bottom-conditions-trainer-win-percent-text',
          ),
          '%from%': Text.get('bottom-conditions-From'),
          '%to%': Text.get('bottom-conditions-to'),
          '%row%': n,
          '%uuid%': percentWinsRow.getUUID(),
        },
      );

      this.addNumberOfRacesInput($row);

      if (percentWinsRow) {
        $row
          .find('.from-points')
          .val(Utils.negativeBlankToValue(percentWinsRow.getWinPercentMin()));
        $row
          .find('.to-points')
          .val(Utils.negativeBlankToValue(percentWinsRow.getWinPercentMax()));
        $row
          .find('.min-races')
          .val(
            Utils.negativeBlankToValue(percentWinsRow.getMinNumberOfRaces()),
          );
        $row
          .find('.max-races')
          .val(
            Utils.negativeBlankToValue(percentWinsRow.getMaxNumberOfRaces()),
          );
      }

      $rows.append($row);
    }

    $wrapper
      .find('input, input[type="number"], select')
      .reevent('change input', (evt, params) => {
        const data = params || {};
        data['type'] = percentRowType;
        data['rowId'] = $(evt.currentTarget)
          .closest('.row')
          .attr('data-row-uuid');
        this.onConditionsChanged(evt, data);
      });
    $wrapper
      .find('.button.plus')
      .reevent('click', this.onAddPercentWinsRowPressed);
    $wrapper
      .find('.button.delete')
      .reevent('click', this.onDeletePercentWinsRowPressed);
    this.attachNegativeBlankEvents($wrapper);
  }

  onAddPercentWinsRowPressed() {
    const reduction = this.getReduction();
    const percentRowType = this.getPercentRowType();
    let groupRow;
    const percentBetween = reduction
      .getJockeyTrainerGroupRows()
      .filter(row => row.getGroupType() === percentRowType);
    if (percentBetween.length === 0) {
      groupRow = reduction.addJockeyTrainerGroupRow();
      groupRow.setGroupType(percentRowType);
    }

    groupRow = reduction.addJockeyTrainerGroupRow();
    groupRow.setGroupType(percentRowType);

    this.getAPI().scheduleBetUpdate();
    this.updateJockeyTrainerWinPercent();

    const $row = this.$conditions.find(
      '.jockey-trainer-conditions-win-percent .rows .row:last-child',
    );
    $row.hide();
    $row.slideDown(300);
  }

  onDeletePercentWinsRowPressed(event) {
    const $target = $(event.currentTarget);
    const $row = $target.closest('.row');
    const uid = $row.attr('data-row-uuid');

    const percentRowType = this.getPercentRowType();

    const reduction = this.getReduction();
    reduction.deleteJockeyTrainerGroupRow(uid);

    const percentBetween = reduction
      .getJockeyTrainerGroupRows()
      .filter(row => row.getGroupType() === percentRowType);

    if (percentBetween.length === 0) {
      const newRow = reduction.addJockeyTrainerGroupRow();
      newRow.setGroupType(percentRowType);
      percentBetween.push(reduction.addJockeyTrainerGroupRow());
    }

    this.getAPI().scheduleBetUpdate();

    if (percentBetween.length === 0) {
      this.updateJockeyTrainerWinPercent();
    } else {
      $row.slideUp(
        300,
        function () {
          this.updateJockeyTrainerWinPercent();
        }.bind(this),
      );
    }
  }

  _getReductionRow(type) {
    const reduction = this.getReduction();
    return reduction
      .getJockeyTrainerGroupRows()
      .filter(row => row.getGroupType() === type);
  }
  // ---------------------------------------- FORM TO DATA ----------------------------------------

  // evt and params is required for the jQuery plugin chosen.
  // params inform us of items that were selected or deselected.
  onConditionsChanged(evt, params) {
    const $form = this.$conditions.find('form');
    const formData = $form.formData();
    const reduction = this.getReduction();

    // Collect all grid rows that has jockey reductions before condition change
    const rowsToUpdate = reduce(
      reduction.getJockeyTrainerGroupRows(),
      function (acc, jockeyRow) {
        if (!jockeyRow.isEmpty()) {
          return reduce(
            jockeyRow.getJockeyTrainerStarts(),
            function (acc, jockeyStart) {
              acc[jockeyStart.getLegNr() - 1] = true;
              return acc;
            },
            {},
          );
        }
        return acc;
      },
      {},
    );

    switch (params.type) {
      case this.getConditionsRowType():
        this._getFormData(
          formData.jockeyTrainerReduction,
          reduction,
          params.type,
          params,
          rowsToUpdate,
        );

        break;

      case JockeyTrainerReductionTypes.HOMETRACK:
        delete params.selected;
        delete params.deselected;
        this._getFormData(
          formData.hometrack,
          reduction,
          params.type,
          params,
          rowsToUpdate,
        );

        break;

      case JockeyTrainerReductionTypes.VICTORY_RICH:
        delete params.selected;
        delete params.deselected;
        this._getFormData(
          formData.victoryRich,
          reduction,
          params.type,
          params,
          rowsToUpdate,
        );

        break;

      case JockeyTrainerReductionTypes.OUT_OF_TOP_TEN:
        delete params.selected;
        delete params.deselected;
        this._getFormData(
          formData.notVictoryRich,
          reduction,
          params.type,
          params,
          rowsToUpdate,
        );

        break;

      case this.getPercentRowType():
        delete params.selected;
        delete params.deselected;
        this._getFormData(
          formData.percentWins,
          reduction,
          params.type,
          params,
          rowsToUpdate,
        );

        break;

      default:
      // Do nothing
    }

    // Update affected grid rows
    const grid = this.getGrid();
    Object.keys(rowsToUpdate).forEach(function (key) {
      grid.updateRow(Number.parseInt(key, 10));
    });

    this.getAPI().scheduleBetUpdate();
  }

  _getFormData(formData, reduction, groupTypeName, params, rowsToUpdate) {
    const jockeyTrainerItems = this.getJockeyTrainerItems();
    const numberOfFormRows = formData ? formData.length : 0;
    const formRows = reduction
      .getJockeyTrainerGroupRows()
      .filter(row => row.getGroupType() === groupTypeName);
    let groupRow;
    let jockeyTrainerData = [];

    for (let rows = 0; rows < numberOfFormRows; rows++) {
      if (rows >= formRows.length) {
        groupRow = reduction.addJockeyTrainerGroupRow();
        groupRow.setGroupType(groupTypeName);
      } else {
        groupRow = formRows[rows];
      }

      if (params.rowId && params.rowId !== groupRow.getUUID()) {
        continue;
      }

      const rowData = formData[rows];
      groupRow.removeAllJockeyTrainers();
      groupRow.setMinNumberOfRaces(
        Utils.valueToNegativeBlank(rowData.minNumberOfRaces || -1),
      );
      groupRow.setMaxNumberOfRaces(
        Utils.valueToNegativeBlank(rowData.maxNumberOfRaces || -1),
      );

      if (
        rowData.winPercentMin &&
        rowData.winPercentMax &&
        !params[groupTypeName]
      ) {
        params[groupTypeName] = orderBy(
          jockeyTrainerItems,
          k => k.lastTwoYearsWinPercent,
          ['desc'],
        )
          .filter(this._filterJockeyTrainerWinPercent(rowData))
          .map(this._getJockeyTrainerNameId);
      }

      groupRow.setWinPercentMin(
        Utils.valueToNegativeBlank(rowData.winPercentMin || -1),
      );
      groupRow.setWinPercentMax(
        Utils.valueToNegativeBlank(rowData.winPercentMax || -1),
      );

      if (params[groupTypeName]) {
        if (params.isMobile) {
          groupRow.removeAllJockeyTrainers();
        }
        jockeyTrainerData = [...params[groupTypeName]];
      }

      jockeyTrainerData.forEach(
        this._addJockeyTrainerUpdateJstarts(
          groupRow,
          jockeyTrainerItems,
          rowsToUpdate,
        ),
      );
    }
  }
  _filterJockeyTrainerWinPercent(rowData) {
    return function (item) {
      return (
        item.roundedLastTwoYearsWinPercent >= rowData.winPercentMin &&
        item.roundedLastTwoYearsWinPercent <= rowData.winPercentMax &&
        item.nrOfStarts >= 1
      );
    };
  }
  _getJockeyTrainerNameId(item) {
    return {
      value: item.id,
      text: item.displayName,
      percent: item.roundedLastTwoYearsWinPercent,
    };
  }
  _addJockeyTrainerUpdateJstarts(groupRow, jockeyTrainerItems, rowsToUpdate) {
    return driver => {
      const jockeyTrainer = find(jockeyTrainerItems, { id: driver.value });
      if (jockeyTrainer) {
        const key = this.isJockeyView ? 'jockey' : 'trainer';
        groupRow.addJockeyTrainers({
          [key]: jockeyTrainer.id,
          starts: jockeyTrainer.jstarts,
        });
        forEach(
          jockeyTrainer.jstarts,
          jstart => (rowsToUpdate[jstart.legNr - 1] = true),
        );
      }
    };
  }
}
